import dayjs from 'dayjs';
import * as _ from 'lodash';
import { CMI5, ROLE } from '../../const';
import * as freeAssignmentcontentPanelService from '../../components/drawer-contents/freeAssignmentContentPanelService';
import * as classroomApi from '../api/classroomApi';
import * as backofficeApi from '../api/backofficeApi';
import { getLogger } from '../logger';
import { getAsset } from '../assets/assetsService';
import { STUDENT_TASK_STATUS } from '../../types/task';

const STATUS_PRIORITY = {
  [STUDENT_TASK_STATUS.TO_REVIEW]: 3,
  [STUDENT_TASK_STATUS.REVIEWED]: 2,
  [STUDENT_TASK_STATUS.PENDING]: 1
};

const debug = getLogger('actions:taskActions');

export function getTaskResultsForInstructor(results) {
  if (!results || _.isEmpty(results)) return {};
  let taskResults = {};
  results.forEach(result => {
    taskResults[result.studentId] = result;
  });
  return taskResults;
}

export function getTaskResultsForStudent(results, studentId) {
  let taskResults = {
    [studentId]: []
  };
  taskResults[studentId] = _.first(results) || { id: studentId, studentId, failed: 0, passed: 0, total: 0 };
  return taskResults;
}

export function getTotalAndPassedActivities(taskResults) {
  const taskResult = _.first(_.values(taskResults));
  if (_.isEmpty(taskResult)) {
    return {
      passedActivities: 0,
      totalActivities: 0
    };
  }
  return {
    passedActivities: taskResult.passed,
    totalActivities: taskResult.passed + taskResult.failed
  };
}

export function getTaskGrade(task, studentId) {
  const studentWasEvaluated =
    task.studentAttempts &&
    task.studentAttempts[studentId] &&
    task.studentAttempts[studentId].evaluation &&
    task.studentAttempts[studentId].evaluation.grade != null;
  if (studentWasEvaluated) {
    return task.studentAttempts[studentId].evaluation.grade * 25;
  }

  let taskResults = task.resultSummary;
  if (studentId && taskResults && !Array.isArray(taskResults) && taskResults[studentId]) {
    taskResults = { [studentId]: taskResults[studentId] };
  }

  let grade = null;
  const isTaskWithActivities = !_.isEmpty(task.activities);
  if (isTaskWithActivities) {
    const totalAndPassedActivities = getTotalAndPassedActivities(taskResults);
    if (totalAndPassedActivities.totalActivities) {
      grade = Math.floor((totalAndPassedActivities.passedActivities / totalAndPassedActivities.totalActivities) * 100);
    }
  }
  return grade;
}

export function getTaskFirstGrade(task, studentId) {
  const studentWasEvaluated =
    task.studentAttempts &&
    task.studentAttempts[studentId] &&
    task.studentAttempts[studentId].responses &&
    task.studentAttempts[studentId].responses[0] &&
    task.studentAttempts[studentId].responses[0].evaluation &&
    task.studentAttempts[studentId].responses[0].evaluation.grade != null;
  if (studentWasEvaluated) {
    return task.studentAttempts[studentId].responses[0].evaluation.grade * 25;
  }

  let taskResults = task.resultSummary;
  let grade = null;
  const isTaskWithActivities = !_.isEmpty(task.activities);
  if (isTaskWithActivities) {
    const totalAndPassedActivities = getTotalAndPassedActivities(taskResults);
    if (totalAndPassedActivities.totalActivities) {
      grade = Math.floor((totalAndPassedActivities.passedActivities / totalAndPassedActivities.totalActivities) * 100);
    }
  }

  return grade;
}

export function getTaskCompletenessForInstructor(task, taskSize) {
  let taskCompleteness = 0;
  let taskResults = task.resultSummary;
  let noLessonActivities = getActivitiesWithoutLessons(task.activities);
  if (!_.isEmpty(noLessonActivities)) {
    _.forIn(_.values(taskResults), taskResult => {
      const finishedResults = getFinishedResults(taskResult);
      if (finishedResults && finishedResults === taskSize) {
        taskCompleteness++;
      }
    });
  } else {
    _.forIn(task.studentAttempts, studentAttempt => {
      if (!_.isEmpty(studentAttempt.responses) || !_.isNil(studentAttempt.evaluation)) {
        taskCompleteness++;
      }
    });
  }

  return taskCompleteness;
}

function getFinishedResults(taskResult) {
  return taskResult.passed + taskResult.failed;
}

export function getTaskCompletenessForStudent(taskResults) {
  const studentIds = _.keys(taskResults);
  let studentCompleteness = 0;

  if (studentIds.length) {
    const studentId = _.first(studentIds);
    studentCompleteness = getFinishedResults(taskResults[studentId]);
  }

  return studentCompleteness;
}

export function getStudentsAssigned(task) {
  if (task.users && task.users.length) {
    return _.filter(task.users, { role: ROLE.STUDENT });
  }
  return [];
}

export function getStudentsForTask(task, classroomParticipants) {
  const isObjectArray = task.users.length > 0 && typeof task.users[0] === 'object';

  if (isObjectArray) {
    const taskUserIds = task.users.map(user => user._id);
    return classroomParticipants.filter(participant => taskUserIds.includes(participant._id));
  } else {
    return classroomParticipants.filter(participant => task.users.includes(participant._id));
  }
}

// Order of checks within classifyContentTaskStudentStatus matters:
// 1. Not complete → PENDING (no point checking further)
// 2. Has open answers pending review → TO_REVIEW
// 3. Everything complete and reviewed → REVIEWED
function classifyContentTaskStudentStatus(studentId, taskResults, task) {
  const totalActivities = getActivitiesWithoutLessons(task.activities).length;
  const summary = taskResults?.[studentId];
  const passedActivities = summary?.passed ?? 0;
  const failedActivities = summary?.failed ?? 0;
  const answeredActivities = summary?.answered ?? 0;
  const contentComplete = totalActivities > 0 && passedActivities + failedActivities + answeredActivities >= totalActivities;

  if (!contentComplete) return { status: STUDENT_TASK_STATUS.PENDING };
  if (answeredActivities > 0) return { status: STUDENT_TASK_STATUS.TO_REVIEW };

  const grade = getTaskGrade(task, studentId);
  return {
    status: STUDENT_TASK_STATUS.REVIEWED,
    passed: typeof grade === 'number' ? grade >= 50 : undefined
  };
}

// Order of checks within classifyFreeResponseStudentStatus matters:
// 1. Has responses and last one is evaluated → REVIEWED (must come before teacherGrade check
//    to avoid marking as REVIEWED when only the global evaluation exists but last response isn't)
// 2. Has top-level teacher grade (evaluation at attempt level) → REVIEWED
// 3. No evaluation at all → PENDING
function classifyFreeResponseStudentStatus(responses, lastResponse, teacherGrade) {
  if (responses.length > 0 && lastResponse?.evaluation) {
    return {
      status: STUDENT_TASK_STATUS.REVIEWED,
      passed: !_.isNil(teacherGrade) ? teacherGrade >= 2 : undefined
    };
  }

  if (!_.isNil(teacherGrade)) {
    return { status: STUDENT_TASK_STATUS.REVIEWED, passed: teacherGrade >= 2 };
  }

  return { status: STUDENT_TASK_STATUS.PENDING };
}

// CRITICAL: The order of checks here is intentional and must not be changed.
// 1. Unevaluated last response → TO_REVIEW: takes priority over everything else.
//    A student who submitted and is awaiting review must never appear as PENDING.
// 2. canStudentRepeat → PENDING: teacher allowed retry, treat as pending regardless of past evaluations.
// 3. Content task → delegate to content-specific classifier (uses activity tracking results).
// 4. Free-response fallback (individual or group).
function classifyStudentStatus(studentId, studentAttempts, taskResults, task) {
  const responses = studentAttempts?.responses || [];
  const lastResponse = responses[responses.length - 1];
  const teacherGrade = studentAttempts?.evaluation?.grade;

  if (responses.length > 0 && lastResponse && !lastResponse.evaluation) {
    return { status: STUDENT_TASK_STATUS.TO_REVIEW };
  }

  if (studentAttempts?.canStudentRepeat) {
    return { status: STUDENT_TASK_STATUS.PENDING };
  }

  const isContentTask = Array.isArray(task.activitiesState) && task.activitiesState.length > 0;
  if (isContentTask) {
    return classifyContentTaskStudentStatus(studentId, taskResults, task);
  }

  return classifyFreeResponseStudentStatus(responses, lastResponse, teacherGrade);
}

export function setStudentsInGroups(students, task, taskResults) {
  let groups = {
    toReview: [],
    reviewed: [],
    pending: []
  };

  let dynamicGroups = {};
  let dynamicGroupStatus = {};
  const studentIds = _.map(students, student => student._id);

  _.forIn(studentIds, studentId => {
    const student = findByStudentId(studentId, students);
    if (!student) return;

    const studentAttempts = task && task.studentAttempts && task.studentAttempts[studentId];
    const { status, passed } = classifyStudentStatus(studentId, studentAttempts, taskResults, task);
    const studentWithPassed = passed !== undefined ? { ...student, passed } : student;

    if (studentAttempts && studentAttempts.taskGroup) {
      const groupKey = `group${studentAttempts.taskGroup}`;
      if (!dynamicGroups[groupKey]) {
        dynamicGroups[groupKey] = [];
      }
      dynamicGroups[groupKey].push(studentWithPassed);
      if (!dynamicGroupStatus[groupKey] || STATUS_PRIORITY[status] > STATUS_PRIORITY[dynamicGroupStatus[groupKey]]) {
        dynamicGroupStatus[groupKey] = status;
      }
      return;
    }

    groups[status].push(studentWithPassed);
  });

  const sortedKeys = Object.keys(dynamicGroups).sort((a, b) => {
    const numA = parseInt(a.replace('group', ''), 10);
    const numB = parseInt(b.replace('group', ''), 10);
    return numA - numB;
  });
  const orderedDynamicGroups = {};
  sortedKeys.forEach(key => {
    orderedDynamicGroups[key] = dynamicGroups[key];
    orderedDynamicGroups[key].status = dynamicGroupStatus[key];
  });

  return { ...groups, ...orderedDynamicGroups, ...addOtherPendingStudents(groups, studentIds, students) };
}

function findByStudentId(studentId, students) {
  return _.first(
    _.filter(students, s => {
      return s._id === studentId;
    })
  );
}

function addOtherPendingStudents(groups, studentIds, students) {
  if (students && studentIds.length === students.length) return groups;
  _.forEach(students, student => {
    if (_.indexOf(studentIds, student._id) < 0) groups.pending.push(student);
  });

  return groups;
}

export let getActivitiesWithoutLessons = activities => {
  return _.compact(
    _.map(activities, act => {
      if (act.activityType !== CMI5.ACTIVITY_TYPE.LESSON) return act.activityId;
    })
  );
};

export let getTaskComments = async (studentId, task, theme) => {
  let comment = {};
  if (!_.isNil(studentId)) {
    const lastComments = task.studentAttempts[studentId]
      ? freeAssignmentcontentPanelService.getLastComment(task.studentAttempts[studentId].comments)
      : undefined;

    if (!_.isNil(lastComments)) {
      let avatar = await freeAssignmentcontentPanelService.getUserAvatar(lastComments.authorId);

      let imageAvatar = avatar;

      if (!isNaN(avatar)) {
        imageAvatar = theme.images.avatars[avatar];
      }

      comment = {
        comment: lastComments.comment,
        avatar: imageAvatar
      };
    }
    return comment;
  }
};

export let updateTaskActivitiesStructure = (taskId, sectionId, classroomId, userId, selectedTask) => {
  let activityStructure = [];
  if (taskId) {
    let requests = [
      classroomApi.getTaskLessonsAndActivities(classroomId, taskId),
      backofficeApi.getActivityStateForStudent(sectionId, _.map(selectedTask.activities, 'activityId'), userId, dayjs())
    ];

    return Promise.all(requests)
      .then(async res => {
        const taskActivitiesResponse = res[0].data;
        const activityStateResponse = res[1].data;

        if (taskActivitiesResponse.activityStructure) {
          let units = taskActivitiesResponse.activityStructure.children;
          activityStructure = getActivityStructure(units, activityStateResponse);
        }
        return activityStructure;
      })
      .catch(err => {
        debug.error('[getSelectedTaskWithResults] error getting activity structure %s', err);
      });
  }
};

export let getActivityStructure = (units, activityStateResponse) => {
  return units.map(unit => {
    let found = _.find(activityStateResponse, { activityId: unit.activityId });
    if (found) {
      unit.success = found.result.success;
    }

    unit.children = unit.children.map(lesson => {
      let found = _.find(activityStateResponse, {
        activityId: lesson.activityId
      });
      if (found && found.result) {
        lesson.success = found.result.success;
      }
      let activities = lesson.children ? lesson.children : [];

      lesson.children = activities.map(activity => {
        let found = _.find(activityStateResponse, {
          activityId: activity.activityId
        });
        if (found && found.result) {
          activity.success = found.result.success;
        }
        return activity;
      });
      return lesson;
    });
    return unit;
  });
};

export const getTaskFiles = async (task, customerId) => {
  if (!task.files) return Promise.reject();
  const getAssetPromises = task.files.map(uuid => getAsset(uuid, customerId));
  const results = await Promise.allSettled(getAssetPromises);
  return results.filter(r => r.status !== 'rejected').map(r => r.value.data);
};

export let updateStudentsFromTask = (classroomId, taskId, studentIds) => {
  classroomApi.updateStudentsFromTask(classroomId, taskId, studentIds);
};

export let searchUserWithAttachments = task => {
  if (!task?.studentAttempts) return [];
  const usersArrayKeys = Object.keys(task.studentAttempts);
  return usersArrayKeys
    .filter(user => {
      const userFind = task.users.find(u => u._id === user);
      return task.studentAttempts[user].responses.length > 0 && userFind !== undefined;
    })
    .map(user => task.users.find(u => u._id === user));
};

export let searchUsersWithComment = task => {
  if (!task?.studentAttempts) return [];
  const usersArrayKeys = Object.keys(task.studentAttempts);
  return usersArrayKeys
    .filter(user => {
      const userFind = task.users.find(u => u._id === user);
      const responses = task.studentAttempts[user].responses;
      return responses.length > 0 && userFind !== undefined && responses[0]?.data;
    })
    .map(user => task.users.find(u => u._id === user));
};
